import { type Sessao, sessaoSchema } from '@fiscoai/contracts';

/**
 * Sessão da interface (ADR 0006):
 *  - o access token vive SÓ em memória — XSS não o encontra depois do reload;
 *  - o refresh token é cookie httpOnly: o JS só chama `POST /api/v1/auth/refresh`;
 *  - renovação single-flight e serializada ENTRE ABAS (Web Locks), senão duas abas
 *    reapresentariam o mesmo refresh e o server derrubaria a sessão como reuso;
 *  - um marcador sem segredo no localStorage diz se vale tentar renovar ao abrir — sem ele, nem
 *    tenta (evita um 401 à toa no console de quem nunca entrou).
 */
const MARGEM_EXPIRACAO_MS = 30_000;
const TRAVA = 'fiscoai:renovar-sessao';
const MARCADOR = 'fiscoai:sessao';
const URL_REFRESH = '/api/v1/auth/refresh';

type Ouvinte = () => void;

function lerMarcador(): boolean {
  try {
    return globalThis.localStorage.getItem(MARCADOR) === '1';
  } catch {
    return false;
  }
}

function gravarMarcador(ativo: boolean): void {
  try {
    if (ativo) globalThis.localStorage.setItem(MARCADOR, '1');
    else globalThis.localStorage.removeItem(MARCADOR);
  } catch {
    // storage bloqueado: só perde a renovação automática ao recarregar
  }
}

export class SessaoStore {
  private token: string | null = null;
  private expiraEm = 0;
  private emAndamento: Promise<boolean> | null = null;
  private readonly ouvintesExpiracao = new Set<Ouvinte>();

  constructor(private readonly fetchImpl: typeof fetch = (...args) => fetch(...args)) {}

  /** Token válido (com margem) ou null — quem chama decide renovar. */
  tokenAtual(agora = Date.now()): string | null {
    return this.token && agora < this.expiraEm - MARGEM_EXPIRACAO_MS ? this.token : null;
  }

  temSessao(): boolean {
    return this.token !== null;
  }

  /** Houve login neste navegador e ele não foi encerrado: vale tentar renovar. */
  provavelmenteAtiva(): boolean {
    return lerMarcador();
  }

  iniciar(sessao: Sessao, agora = Date.now()): void {
    this.token = sessao.accessToken;
    this.expiraEm = agora + sessao.expiresIn * 1000;
    gravarMarcador(true);
  }

  encerrar(): void {
    this.token = null;
    this.expiraEm = 0;
    gravarMarcador(false);
  }

  aoExpirar(ouvinte: Ouvinte): () => void {
    this.ouvintesExpiracao.add(ouvinte);
    return () => this.ouvintesExpiracao.delete(ouvinte);
  }

  avisarExpirada(): void {
    this.encerrar();
    for (const ouvinte of this.ouvintesExpiracao) ouvinte();
  }

  /** Renova o access token pelo cookie. `true` se a sessão continua válida. */
  renovar(): Promise<boolean> {
    this.emAndamento ??= this.renovarSerializado().finally(() => {
      this.emAndamento = null;
    });
    return this.emAndamento;
  }

  private renovarSerializado(): Promise<boolean> {
    const travas = typeof navigator === 'undefined' ? undefined : navigator.locks;
    const tokenAntes = this.token;
    const executar = () => this.chamarRefresh(tokenAntes);
    return travas ? travas.request(TRAVA, executar) : executar();
  }

  private async chamarRefresh(tokenAntes: string | null): Promise<boolean> {
    // Outra chamada desta aba já renovou enquanto esperávamos a trava.
    if (this.token !== tokenAntes && this.tokenAtual()) return true;
    try {
      const resposta = await this.fetchImpl(URL_REFRESH, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!resposta.ok) {
        this.encerrar();
        return false;
      }
      this.iniciar(sessaoSchema.parse(await resposta.json()));
      return true;
    } catch {
      this.encerrar();
      return false;
    }
  }
}

/** Instância única. Testes criam a própria com `fetch` falso. */
export const sessao = new SessaoStore();
